import type { SalesPointConfigurationResponseDTO } from '@/types/dto/agent';
import {
  deriveAgeFromPassengerType,
  type PosPassengerType,
} from '@/utils/passengerType';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import {
  DEPARTURE_DATE_FORM_FIELD_NAME,
  DESTINATION_FORM_FIELD_NAME,
  ORIGIN_FORM_FIELD_NAME,
  PASSENGERS_FORM_FIELD_NAME,
  PRODUCT_TYPE_FORM_FIELD_NAME,
  PROMO_CODES_FORM_FIELD_NAME,
  RETURN_DATE_FORM_FIELD_NAME,
} from '@packages/webshop-search-widget';
import { v4 } from 'uuid';

export const parseUrlSearchParams = (
  passengerTypes: SalesPointConfigurationResponseDTO['passengerTypes']
) => {
  const searchParams = new URLSearchParams(window.location.search);

  const productType = searchParams.get(PRODUCT_TYPE_FORM_FIELD_NAME);
  const origin = searchParams.get(ORIGIN_FORM_FIELD_NAME);
  const destination = searchParams.get(DESTINATION_FORM_FIELD_NAME);
  const departureDate = searchParams.get(DEPARTURE_DATE_FORM_FIELD_NAME);
  const returnDate = searchParams.get(RETURN_DATE_FORM_FIELD_NAME);
  const passengers = searchParams.get(PASSENGERS_FORM_FIELD_NAME);
  const promoCodes = searchParams.get(PROMO_CODES_FORM_FIELD_NAME);

  return {
    productType: parseProductType(productType),
    origin: parseStop(origin),
    destination: parseStop(destination),
    departureDate: parseDate(departureDate),
    returnDate: parseDate(returnDate),
    passengers: parsePassengers(passengers, passengerTypes),
    promoCodes: parsePromoCodes(promoCodes),
  };
};

const parsePromoCodes = (
  promoCodes: string | null
): SearchFormValues['promoCodes'] => {
  try {
    const parsedPromoCodes: SearchFormValues['promoCodes'] = promoCodes
      ? JSON.parse(promoCodes)
      : [];

    if (!Array.isArray(parsedPromoCodes)) {
      return [];
    }

    const filteredAppliedPromoCodes: SearchFormValues['promoCodes'] = [];

    parsedPromoCodes.forEach(({ code, name, description }) => {
      if (code && name && description) {
        filteredAppliedPromoCodes.push({ code, name, description });
      }
    });

    return filteredAppliedPromoCodes;
  } catch {
    return [];
  }
};

const parseStop = (
  stop: string | null
): NonNullable<SearchFormValues['origin']> | undefined => {
  try {
    const parsedStop = stop && JSON.parse(stop);

    if (
      typeof parsedStop?.value !== 'string' ||
      typeof parsedStop?.label !== 'string'
    ) {
      return undefined;
    }

    const type =
      parsedStop?.type === 'address' || parsedStop?.type === 'stop'
        ? parsedStop.type
        : 'stop';

    return {
      value: parsedStop.value,
      label: parsedStop.label,
      type,
      ...(typeof parsedStop?.cityName === 'string'
        ? { cityName: parsedStop.cityName }
        : {}),
      ...(typeof parsedStop?.geoPosition?.latitude === 'number' &&
      typeof parsedStop?.geoPosition?.longitude === 'number'
        ? {
            geoPosition: {
              latitude: parsedStop.geoPosition.latitude,
              longitude: parsedStop.geoPosition.longitude,
            },
          }
        : {}),
    };
  } catch {
    return undefined;
  }
};

const parsePassengers = (
  passengers: string | null,
  passengerTypes: SalesPointConfigurationResponseDTO['passengerTypes']
): SearchFormValues['passengers'] | undefined => {
  try {
    const parsedPassengers: Array<
      Partial<SearchFormValues['passengers'][number]>
    > = passengers ? JSON.parse(passengers) : [];

    if (!Array.isArray(parsedPassengers)) {
      return undefined;
    }

    const filteredPassengers: SearchFormValues['passengers'] = [];

    parsedPassengers.forEach(({ passengerTypeId, externalReference, age }) => {
      const passengerType: PosPassengerType | undefined = passengerTypes?.find(
        ({ id }) => id === passengerTypeId
      );

      if (passengerTypeId && passengerType) {
        // `isDefault` and the age both describe the passenger *type*, so
        // take them from the sales point's catalogue rather than from the
        // link — a handoff link names a `passengerTypeId` and nothing else.
        // The widget's schema requires an age on every non-default typed
        // passenger, and it was previously left undefined here: the
        // deep-linked first search still ran (`SearchWidgetWrapper` submits
        // it without the resolver), but every later Search click failed
        // validation silently, so a promo code the customer added on the
        // results page never reached a new offer search (BR-58550).
        //
        // Only a non-default type needs an age, so a default-type link
        // keeps the ageless roster it already submits today.
        const resolvedAge =
          typeof age === 'number'
            ? age
            : passengerType.isDefault
              ? undefined
              : deriveAgeFromPassengerType(passengerType);

        filteredPassengers.push({
          passengerTypeId,
          externalReference: externalReference ?? v4(),
          type: 'PERSON',
          isDefault: passengerType.isDefault ?? false,
          ...(resolvedAge !== undefined ? { age: resolvedAge } : {}),
        });
      }
    });

    return filteredPassengers;
  } catch {
    return [];
  }
};

const parseDate = (date: string | null): Date | undefined => {
  const parsedDate = date && new Date(date);

  return parsedDate instanceof Date ? parsedDate : undefined;
};

const parseProductType = (
  value: string | null
): SearchFormValues['productType'] | undefined =>
  value === 'ticket' || value === 'travelPass' ? value : undefined;
